import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface ConfettiPiece {
  id: number
  startX: number
  startY: number
  endX: number
  endY: number
  rotation: number
  scale: number
  color: string
  delay: number
  shape: 'square' | 'circle' | 'triangle'
}

interface ConfettiProps {
  isActive: boolean
}

const Confetti: React.FC<ConfettiProps> = ({ isActive }) => {
  const [pieces, setPieces] = useState<ConfettiPiece[]>([])

  const colors = [
    '#FF6B6B', // красный
    '#4ECDC4', // бирюзовый
    '#45B7D1', // голубой
    '#96CEB4', // зеленый
    '#FFEAA7', // желтый
    '#DDA0DD', // фиолетовый
    '#FFB347', // оранжевый
    '#87CEEB', // небесно-голубой
    '#FF69B4', // розовый
    '#32CD32', // лаймовый
    '#FFD700', // золотой
    '#FF4500', // красно-оранжевый
  ]

  useEffect(() => {
    if (isActive) {
      const screenWidth = window.innerWidth
      const screenHeight = window.innerHeight
      
      const newPieces: ConfettiPiece[] = []
      const shapes: ('square' | 'circle' | 'triangle')[] = ['square', 'circle', 'triangle']
      
      // Конфетти с левого края (50 штук)
      for (let i = 0; i < 50; i++) {
        const startX = -100 - Math.random() * 50 // За левым краем экрана
        const startY = Math.random() * screenHeight
        const endX = startX + 200 + Math.random() * (screenWidth * 0.6) // Летят вправо
        const endY = startY + 100 + Math.random() * (screenHeight * 0.8) // Летят вниз
        
        newPieces.push({
          id: i,
          startX,
          startY,
          endX,
          endY,
          rotation: Math.random() * 360,
          scale: Math.random() * 0.6 + 0.4,
          color: colors[Math.floor(Math.random() * colors.length)],
          delay: Math.random() * 0.8,
          shape: shapes[Math.floor(Math.random() * shapes.length)],
        })
      }
      
      // Конфетти с правого края (50 штук)
      for (let i = 50; i < 100; i++) {
        const startX = screenWidth + 50 + Math.random() * 50 // За правым краем экрана
        const startY = Math.random() * screenHeight
        const endX = startX - 200 - Math.random() * (screenWidth * 0.6) // Летят влево
        const endY = startY + 100 + Math.random() * (screenHeight * 0.8) // Летят вниз
        
        newPieces.push({
          id: i,
          startX,
          startY,
          endX,
          endY,
          rotation: Math.random() * 360,
          scale: Math.random() * 0.6 + 0.4,
          color: colors[Math.floor(Math.random() * colors.length)],
          delay: Math.random() * 0.8,
          shape: shapes[Math.floor(Math.random() * shapes.length)],
        })
      }
      
      // Дополнительное конфетти сверху (30 штук)
      for (let i = 100; i < 130; i++) {
        const startX = Math.random() * screenWidth
        const startY = -100 - Math.random() * 50 // За верхним краем экрана
        const endX = startX + (Math.random() - 0.5) * (screenWidth * 0.4) // Случайное движение влево-вправо
        const endY = startY + 200 + Math.random() * (screenHeight * 0.7) // Летят вниз
        
        newPieces.push({
          id: i,
          startX,
          startY,
          endX,
          endY,
          rotation: Math.random() * 360,
          scale: Math.random() * 0.6 + 0.4,
          color: colors[Math.floor(Math.random() * colors.length)],
          delay: Math.random() * 0.6,
          shape: shapes[Math.floor(Math.random() * shapes.length)],
        })
      }
      

      
      setPieces(newPieces)
    } else {
      setPieces([])
    }
  }, [isActive])

  return (
    <AnimatePresence>
      {isActive && (
        <div className="fixed inset-0 pointer-events-none z-[999999] overflow-hidden">
          {pieces.map((piece) => (
            <motion.div
              key={piece.id}
              className={`absolute ${
                piece.shape === 'circle' ? 'rounded-full' : 
                piece.shape === 'triangle' ? 'transform rotate-45' : 'rounded-sm'
              }`}
              style={{
                backgroundColor: piece.color,
                width: piece.shape === 'triangle' ? '10px' : '14px',
                height: piece.shape === 'triangle' ? '10px' : '14px',
              }}
              initial={{
                x: piece.startX,
                y: piece.startY,
                rotate: piece.rotation,
                scale: 0,
                opacity: 0,
              }}
              animate={{
                x: piece.endX,
                y: piece.endY,
                rotate: piece.rotation + 720, // Два полных оборота
                scale: piece.scale,
                opacity: [0, 1, 1, 0], // Появляется, держится, исчезает
              }}
              transition={{
                duration: 3 + Math.random() * 2, // 3-5 секунд
                delay: piece.delay,
                ease: "easeOut",
                opacity: {
                  duration: 3 + Math.random() * 2,
                  times: [0, 0.1, 0.8, 1], // Быстро появляется, долго держится, медленно исчезает
                }
              }}
              exit={{
                opacity: 0,
                scale: 0,
                transition: { duration: 0.2 }
              }}
            />
          ))}
        </div>
      )}
    </AnimatePresence>
  )
}

export default Confetti 